import { JxsComponent } from '@jxs/component';
import { JxsScrollTrigger, gsap } from '@jxs/gsap';
import { FrameLoader, CanvasRenderer, CloudinaryProvider } from '@jxs/image-sequence';
import { debounce } from '@jxs/utilities';

/**
 * @typedef {Object} JxsStVideoFollowRefs
 * @property {HTMLElement} viewport
 * @property {HTMLCanvasElement} canvas
 * @property {HTMLElement} overlay
 * @property {HTMLElement[]} items
 */

/**
 * @typedef {Object} JxsStVideoFollowConfig
 * @property {number} width - 加载桌面端图片的要求宽度
 * @property {number} mobileWidth - 加载移动端图片的要求宽度
 * @property {number} distancePerSecond - 每秒视频对应的滚动距离（px）
 * @property {number} mobileBreakpoint - 移动端断点宽度（px）
 */

/** @extends {JxsComponent<JxsStVideoFollowRefs>} */
class JxsStVideoFollow extends JxsComponent {
  /**
   * 配置参数
   * @type {JxsStVideoFollowConfig}
   */
  #CONFIG = {
    width: 1920,
    mobileWidth: 960,
    distancePerSecond: 460,
    mobileBreakpoint: 767
  };

  /** @type {FrameLoader|null} */
  #frameLoader = null;

  /** @type {CanvasRenderer|null} */
  #canvasRenderer = null;

  /** @type {JxsScrollTrigger|null} */
  #scroller = null;

  constructor() {
    super();

    /** @type {string|null} */
    this.cloudinaryVideoUrl = /** @type {string|null} */ (this.getDataset('cloudinaryVideoUrl'));
    /** @type {number} */
    this.videoDuration = Number(this.getDataset('videoDuration')) || 5;
    /** @type {string|null} */
    this.mobileCloudinaryVideoUrl = /** @type {string|null} */ (this.getDataset('mobileCloudinaryVideoUrl'));
    /** @type {number} */
    this.mobileVideoDuration = Number(this.getDataset('mobileVideoDuration')) || 5;

    if (!Shopify.designMode) {
      this.removeAttribute('data-design-mode');
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroy();
  }

  /**
   * 检测是否为移动端
   * @returns {boolean}
   */
  #checkIsMobile() {
    return window.innerWidth < this.#CONFIG.mobileBreakpoint;
  }

  /**
   * 获取当前使用的视频配置
   * @returns {{ url: string|null, duration: number, width: number }}
   */
  #getCurrentVideoConfig() {
    const isMobile = this.#checkIsMobile();

    if (isMobile && this.mobileCloudinaryVideoUrl) {
      return {
        url: this.mobileCloudinaryVideoUrl,
        duration: this.mobileVideoDuration,
        width: this.#CONFIG.mobileWidth,
      };
    }

    return {
      url: this.cloudinaryVideoUrl,
      duration: this.videoDuration,
      width: this.#CONFIG.width,
    };
  }

  /**
   * 窗口大小变化处理
   */
  #onResize = debounce(() => {
    this.#canvasRenderer?.resize();
  }, 200);


  /**
   * 初始化帧加载器
   * @returns {FrameLoader|null}
   */
  #initFrameLoader() {
    const config = this.#getCurrentVideoConfig();

    if (!config.url) {
      console.warn('[JxsStVideoFollow] 未提供 Cloudinary URL');
      return null;
    }

    const frameProvider = new CloudinaryProvider(config.url, {
      duration: config.duration,
      width: config.width,
    });
    const frameLoader = new FrameLoader(frameProvider);

    return frameLoader;
  }

  /**
   * 初始化组件
   */
  async init() {
    this.destroy();

    this.#frameLoader = this.#initFrameLoader();

    if (!this.#frameLoader || !this.refs.canvas) {
      return;
    }

    this.#canvasRenderer = new CanvasRenderer(this.refs.canvas, this.#frameLoader);
    this.#canvasRenderer.waitForReady();


    const config = this.#getCurrentVideoConfig();

    const distancePerSecond = this.#CONFIG.distancePerSecond;
    const headingDuration = 2;
    const videoDuration = config.duration;

    const totalDuration = headingDuration + videoDuration;

    this.#scroller = new JxsScrollTrigger(this.refs.viewport, {
      trigger: this.refs.viewport,
      distance: totalDuration * distancePerSecond,
      scrub: 0
    });

    const overlayEl = this.refs.overlay;
    const heading = overlayEl?.querySelector(':scope > .jxs-block-text')
    const videoProxy = { progress: 0 };

    const tl = this.#scroller.timeline;

    tl.to(overlayEl, {
      opacity: 0,
      duration: headingDuration,
      ease: 'power2.inOut',
    }, 0);
    if (heading) {
      tl.to(heading, {
        y: -50,
        scale: 1.5,
        duration: headingDuration
      }, 0)
    }
    tl.to(videoProxy, {
      duration: videoDuration,
      progress: 1,
      ease: 'none',
      onUpdate: () => {
        if (this.#canvasRenderer) {
          this.#canvasRenderer.render(videoProxy.progress);
        }
      }
    }, '<1');

    this.refs.items.forEach(item => { 
      const startDuration = Number(item.dataset.startDuration) || 0;
      // 减去出现动画占用的0.5秒
      const holdDuration = (Number(item.dataset.holdDuration) || 0) - 0.5;
      tl
        .to(item, {
          opacity: 1,
          duration: 0.5,
          y: -50,
          ease: 'power2.inOut',
        }, startDuration)
        .to(item, {
          opacity: 0,
          y: 0,
          duration: 0.5,
          ease: 'power2.inOut',
        }, '>' + holdDuration);
    });

    window.addEventListener('resize', this.#onResize);
  }

  /**
   * 销毁组件
   */
  destroy() {

    if (this.#frameLoader) {
      this.#frameLoader.destroy();
      this.#frameLoader = null;
    }

    if (this.#canvasRenderer) {
      this.#canvasRenderer.destroy();
      this.#canvasRenderer = null;
    }

    if (this.#scroller) {
      this.#scroller.destroy();
      this.#scroller = null;
    }

    this.#onResize.cancel();
    window.removeEventListener('resize', this.#onResize);
  }
}

if (!customElements.get('jxs-st-video-follow')) {
  customElements.define('jxs-st-video-follow', JxsStVideoFollow);
}